import { useState, useEffect } from 'react';

export type ViewportSize = 'mobile' | 'tablet' | 'desktop' | 'large';

interface UseResponsiveOptions {
  mobileBreakpoint?: number;
  tabletBreakpoint?: number;
  desktopBreakpoint?: number;
}

export function useResponsive(options: UseResponsiveOptions = {}) {
  const { mobileBreakpoint = 768, tabletBreakpoint = 1024, desktopBreakpoint = 1440 } = options;

  const [viewportSize, setViewportSize] = useState<ViewportSize | null>(null);

  useEffect(() => {
    const getViewportSize = (): ViewportSize => {
      if (typeof window === 'undefined') return 'desktop';

      const width = window.innerWidth;

      if (width < mobileBreakpoint) return 'mobile';
      if (width < tabletBreakpoint) return 'tablet';
      if (width < desktopBreakpoint) return 'desktop';
      return 'large';
    };

    const updateViewportSize = () => {
      setViewportSize(getViewportSize());
    };

    updateViewportSize();

    window.addEventListener('resize', updateViewportSize);
    window.addEventListener('orientationchange', updateViewportSize);

    return () => {
      window.removeEventListener('resize', updateViewportSize);
      window.removeEventListener('orientationchange', updateViewportSize);
    };
  }, [mobileBreakpoint, tabletBreakpoint, desktopBreakpoint]);

  // Convenience getters
  const isMobile = viewportSize === 'mobile';
  const isTablet = viewportSize === 'tablet';
  const isDesktop = viewportSize === 'desktop';
  const isLarge = viewportSize === 'large';
  const isMobileOrTablet = viewportSize === 'mobile' || viewportSize === 'tablet';
  const isDesktopOrLarger = viewportSize === 'desktop' || viewportSize === 'large';

  return {
    viewportSize,
    isMobile,
    isTablet,
    isDesktop,
    isLarge,
    isMobileOrTablet,
    isDesktopOrLarger
  };
}
